import { supabase } from "@/lib/supabaseClient";

// ---------------------------------------------------------------------------
// Admin > WhatsApp: conexion con la API de WhatsApp Cloud, plantillas, agente de IA
// y bandeja. Las credenciales NUNCA llegan al navegador: viven en wa_credentials
// (solo service role) y se manejan desde la funcion wa-admin.
// ---------------------------------------------------------------------------

export type Ok<T = object> = ({ ok: true } & T) | { ok: false; error: string };

async function waAdmin<T = Record<string, unknown>>(action: string, payload: Record<string, unknown> = {}): Promise<T & { ok?: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("wa-admin", { body: { action, ...payload } });
  if (error) {
    // 4xx/5xx devuelven el cuerpo dentro del error; lo intentamos leer.
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        const b = await ctx.json();
        return { ok: false, error: b?.error === "forbidden" ? "Solo administradores." : String(b?.error ?? "network") } as T & { ok?: boolean; error?: string };
      }
    } catch { /* ignore */ }
    return { ok: false, error: "No se pudo conectar con el servidor." } as T & { ok?: boolean; error?: string };
  }
  return data as T & { ok?: boolean; error?: string };
}

let orgCache: string | null = null;
async function orgId(): Promise<string> {
  if (orgCache) return orgCache;
  const { data: u } = await supabase.auth.getUser();
  const uid = u.user?.id;
  if (!uid) throw new Error("Sin sesión");
  const { data, error } = await supabase.from("profiles").select("organization_id").eq("id", uid).maybeSingle();
  if (error || !data?.organization_id) throw error ?? new Error("Sin organización");
  orgCache = data.organization_id as string;
  return orgCache;
}

// ------------------------------------------------------------------ Conexion
export type WaStatus = {
  hasToken: boolean;
  hasAppSecret: boolean;
  phoneNumberId: string | null;
  wabaId: string | null;
  verifyToken: string;
  webhookUrl: string;
  displayPhone: string | null;
  verifiedName: string | null;
  quality: string | null;
  ollama: boolean;
  connection: "not_configured" | "ok" | "error";
  error: string | null;
};

export const fetchWaStatus = () => waAdmin<WaStatus>("status");
export const saveWaCredentials = (c: { accessToken?: string; phoneNumberId?: string; wabaId?: string; appSecret?: string }) => waAdmin("save_credentials", c);
export const clearWaCredentials = () => waAdmin("clear_credentials");
export const regenVerifyToken = () => waAdmin<{ verifyToken: string }>("regen_verify_token");
export const subscribeWaApp = () => waAdmin("subscribe");

// --------------------------------------------------------------- Plantillas
export type TemplateButton = { type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER"; text: string; url?: string; phone_number?: string };

export type WaTemplate = {
  id: string;
  name: string;
  language: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION";
  headerText: string | null;
  body: string;
  footer: string | null;
  buttons: TemplateButton[];
  examples: string[];
  status: string;
  reason: string | null;
  lastSyncedAt: string | null;
};

type TemplateRow = {
  id: string; name: string; language: string; category: string; header_text: string | null; body: string; footer: string | null;
  buttons: unknown; examples: unknown; status: string; reason: string | null; last_synced_at: string | null;
};

const mapTemplate = (r: TemplateRow): WaTemplate => ({
  id: r.id,
  name: r.name,
  language: r.language,
  category: (r.category as WaTemplate["category"]) ?? "UTILITY",
  headerText: r.header_text,
  body: r.body,
  footer: r.footer,
  buttons: Array.isArray(r.buttons) ? (r.buttons as TemplateButton[]) : [],
  examples: Array.isArray(r.examples) ? (r.examples as unknown[]).map(String) : [],
  status: r.status,
  reason: r.reason,
  lastSyncedAt: r.last_synced_at,
});

export async function fetchTemplates(): Promise<WaTemplate[]> {
  const { data, error } = await supabase.from("wa_templates").select("*").order("created_at", { ascending: true });
  if (error) throw error;
  return (data as TemplateRow[]).map(mapTemplate);
}

export async function saveTemplateDraft(t: Partial<WaTemplate> & { name: string; body: string }): Promise<void> {
  const org = await orgId();
  const row = {
    organization_id: org,
    name: t.name.trim(),
    language: t.language ?? "es",
    category: t.category ?? "UTILITY",
    header_text: t.headerText?.trim() || null,
    body: t.body.trim(),
    footer: t.footer?.trim() || null,
    buttons: t.buttons ?? [],
    examples: t.examples ?? [],
    status: "DRAFT",
    reason: null,
  };
  if (t.id) {
    const { error } = await supabase.from("wa_templates").update(row).eq("id", t.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("wa_templates").insert(row);
    if (error) throw error;
  }
}

export const syncTemplates = () => waAdmin<{ count: number }>("templates_sync");
export const seedWebinarTemplates = () => waAdmin("seed_webinar_templates");
export const submitTemplate = (id: string) => waAdmin<{ status: string }>("template_submit", { id });
export const deleteTemplate = (id: string) => waAdmin("template_delete", { id });
export const sendTemplate = (p: { to: string; name: string; language: string; params: string[] }) => waAdmin("send_template", p);

// ------------------------------------------------------------------- Agente
export type WaAgent = {
  enabled: boolean;
  agentName: string;
  tone: string;
  goal: string;
  instructions: string;
  businessContext: string;
  forbidden: string;
  greeting: string;
  fallback: string;
  handoffKeywords: string;
  maxAutoReplies: number;
  replyDelaySeconds: number;
};

export const DEFAULT_AGENT: WaAgent = {
  enabled: false,
  agentName: "Asistente Vívelo",
  tone: "Cercano, amable y claro. Tuteo. Mensajes cortos.",
  goal: "Resolver dudas sobre Vívelo y el webinar, y guiar a la persona a ver el webinar completo y hacer el test.",
  instructions: "",
  businessContext: "",
  forbidden: "No prometas ingresos ni resultados económicos. No uses \"libertad financiera\" ni \"ganar dinero rápido\". No inventes precios, fechas ni datos que no estén en el conocimiento. No pidas contraseñas, tarjetas ni documentos.",
  greeting: "Hola {nombre}, soy {agente} de Vívelo. ¿En qué te puedo ayudar?",
  fallback: "Gracias por escribirnos. Una persona del equipo te responderá muy pronto.",
  handoffKeywords: "asesor, humano, persona, hablar con alguien, queja, reclamo, cancelar, estafa, denuncia, abogado",
  maxAutoReplies: 6,
  replyDelaySeconds: 4,
};

export async function fetchAgent(): Promise<WaAgent> {
  const { data, error } = await supabase.from("wa_agent").select("*").maybeSingle();
  if (error) throw error;
  if (!data) return DEFAULT_AGENT;
  return {
    enabled: data.enabled,
    agentName: data.agent_name,
    tone: data.tone,
    goal: data.goal,
    instructions: data.instructions,
    businessContext: data.business_context,
    forbidden: data.forbidden,
    greeting: data.greeting,
    fallback: data.fallback,
    handoffKeywords: data.handoff_keywords,
    maxAutoReplies: data.max_auto_replies,
    replyDelaySeconds: data.reply_delay_seconds,
  };
}

export async function saveAgent(a: WaAgent): Promise<void> {
  const org = await orgId();
  const { error } = await supabase.from("wa_agent").upsert(
    {
      organization_id: org,
      enabled: a.enabled,
      agent_name: a.agentName.trim() || DEFAULT_AGENT.agentName,
      tone: a.tone,
      goal: a.goal,
      instructions: a.instructions,
      business_context: a.businessContext,
      forbidden: a.forbidden,
      greeting: a.greeting,
      fallback: a.fallback,
      handoff_keywords: a.handoffKeywords,
      max_auto_replies: Math.min(30, Math.max(1, Math.round(a.maxAutoReplies))),
      reply_delay_seconds: Math.min(60, Math.max(0, Math.round(a.replyDelaySeconds))),
      updated_at: new Date().toISOString(),
    },
    { onConflict: "organization_id" },
  );
  if (error) throw error;
}

export type KnowledgeItem = { id: string; title: string; question: string; answer: string; active: boolean };

export async function fetchKnowledge(): Promise<KnowledgeItem[]> {
  const { data, error } = await supabase.from("wa_knowledge").select("id, title, question, answer, active").order("created_at", { ascending: true });
  if (error) throw error;
  return data as KnowledgeItem[];
}

export async function saveKnowledge(k: { id?: string; title: string; question: string; answer: string; active: boolean }): Promise<void> {
  const row = { title: k.title.trim(), question: k.question.trim(), answer: k.answer.trim(), active: k.active };
  if (k.id) {
    const { error } = await supabase.from("wa_knowledge").update(row).eq("id", k.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("wa_knowledge").insert({ ...row, organization_id: await orgId() });
    if (error) throw error;
  }
}

export async function deleteKnowledge(id: string): Promise<void> {
  const { error } = await supabase.from("wa_knowledge").delete().eq("id", id);
  if (error) throw error;
}

export type TestTurn = { role: "user" | "assistant"; content: string };
export const testAgent = (messages: TestTurn[], name: string) =>
  waAdmin<{ reply: string; handoff: boolean; reason: string; model: string }>("agent_test", { messages, name });

// ------------------------------------------------------------------ Bandeja
export type WaConversation = {
  id: string;
  phone: string;
  name: string | null;
  status: "bot" | "human" | "closed";
  autoReplies: number;
  lastInboundAt: string | null;
  lastMessageAt: string;
  preview: string | null;
  unread: number;
  handoffReason: string | null;
};

export type WaMessage = {
  id: string;
  direction: "in" | "out";
  sender: "contact" | "bot" | "human" | "template";
  body: string;
  templateName: string | null;
  status: string | null;
  error: string | null;
  createdAt: string;
};

export async function fetchConversations(): Promise<WaConversation[]> {
  const { data, error } = await supabase.from("wa_conversations").select("*").order("last_message_at", { ascending: false }).limit(200);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    phone: r.phone,
    name: r.contact_name,
    status: r.status,
    autoReplies: r.auto_replies,
    lastInboundAt: r.last_inbound_at,
    lastMessageAt: r.last_message_at,
    preview: r.last_preview,
    unread: r.unread,
    handoffReason: r.handoff_reason,
  }));
}

export async function fetchMessages(conversationId: string): Promise<WaMessage[]> {
  const { data, error } = await supabase
    .from("wa_messages")
    .select("id, direction, sender, body, template_name, status, error, created_at")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true })
    .limit(300);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    direction: r.direction,
    sender: r.sender,
    body: r.body,
    templateName: r.template_name,
    status: r.status,
    error: r.error,
    createdAt: r.created_at,
  }));
}

export async function setConversationStatus(id: string, status: "bot" | "human" | "closed"): Promise<void> {
  const patch: Record<string, unknown> = { status };
  if (status === "bot") {
    patch.auto_replies = 0;
    patch.handoff_reason = null;
  }
  const { error } = await supabase.from("wa_conversations").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteConversation(id: string): Promise<void> {
  const { error } = await supabase.from("wa_conversations").delete().eq("id", id);
  if (error) throw error;
}

export async function markConversationRead(id: string): Promise<void> {
  await supabase.from("wa_conversations").update({ unread: 0 }).eq("id", id);
}

export const sendHumanText = (conversationId: string, text: string) => waAdmin("send_text", { conversation_id: conversationId, text });

/** ¿Sigue abierta la ventana de 24 h para responder con texto libre? */
export const windowOpen = (lastInboundAt: string | null) => !!lastInboundAt && Date.now() - Date.parse(lastInboundAt) < 24 * 3600 * 1000;
